import { useState } from 'react'
import type { AllocationInfo, OverflowPoolInfo } from '@/types/api'
import { Card } from '@/components/shared/Card'
import { Button } from '@/components/shared/Button'
import { StatusBadge } from './StatusBadge'
import { OverflowStatus } from './OverflowStatus'
import { RequestCapModal } from './RequestCapModal'
import { formatCurrency } from '@/utils/formatters'

interface AffiliateGeoCardProps {
  allocation: AllocationInfo
  readOnly?: boolean
  /** Endpoint the Request Cap modal posts to (share link passes its token route). */
  requestPath?: string
  pool?: OverflowPoolInfo
  /** The offer's cap unit — a request on a conversion offer (Reco) must ask for a cv
   *  count, not a $ amount: the server interprets and the admin approves in this unit. */
  capUnit?: 'usd' | 'conversions'
  /** Set when the selected offer is admin-paused — the server refuses new cap_requests
   *  while true, so Request Cap is disabled here instead of only surfacing the 409
   *  after submit. */
  paused?: boolean
}

/**
 * Affiliate-facing geo card — a single revenue-$ cap tracker (all offers unified on $;
 * conversion offers are converted to $ server-side). All values are net (post-throttle).
 * Give-up is handled by the top-level GiveUpFunnel (Give Up → offer → geo → amount),
 * so affiliates don't hunt for a specific card.
 */
export function AffiliateGeoCard({ allocation, readOnly, requestPath, pool, capUnit = 'usd', paused = false }: AffiliateGeoCardProps) {
  const [showRequestCap, setShowRequestCap] = useState(false)

  const usedPct = Math.min(100, Math.max(0, allocation.budget_pct ?? 0))
  const total = allocation.cap_total ?? 0
  const used = allocation.cap_used ?? 0
  const remaining = allocation.cap_remaining ?? Math.max(0, total - used)

  // Set only for affiliates on a shared cap pool; when it is, `total`/`used`/`remaining`
  // above are already the team's — see `AllocationInfo.team`.
  const team = allocation.team ?? null

  const barColor = usedPct >= 95 ? 'bg-brand-red' : usedPct >= 75 ? 'bg-brand-yellow' : 'bg-brand-green'
  const drawingOverflow =
    allocation.status === 'OVERFLOW_ACTIVE' || (allocation.overflow_used ?? 0) > 0

  return (
    <Card className="animate-fade-in flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center">
            <span className="font-semibold text-xs text-text-primary">{allocation.geo_code}</span>
          </div>
          <h3 className="font-semibold text-sm text-text-primary">{allocation.geo_name}</h3>
        </div>
        <StatusBadge status={allocation.status} />
      </div>

      {/* Remaining headline ($) */}
      <div className="mb-3">
        <p className="text-[11px] uppercase tracking-wide text-text-muted">
          {team ? 'Team remaining cap' : 'Remaining cap'}
        </p>
        <p className="text-xl font-semibold tabular-nums text-text-primary leading-tight">{formatCurrency(remaining)}</p>
        {team && (
          <p className="text-[11px] text-text-muted mt-0.5">
            Shared pool with{' '}
            <span className="font-semibold text-text-secondary">
              {team.partner_names.length ? team.partner_names.join(' · ') : `${team.member_count - 1} partner(s)`}
            </span>
            {' · '}any of you can spend it
          </p>
        )}
      </div>

      {/* Used / total bar ($) */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5 text-xs">
          <span className="text-text-muted">
            {team ? 'Team used ' : 'Used '}
            <span className="font-semibold text-text-secondary">{formatCurrency(used)}</span>
          </span>
          <span className="font-semibold text-text-secondary">{usedPct}%</span>
        </div>
        <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ease-out ${barColor}`} style={{ width: `${usedPct}%` }} />
        </div>
        <p className="text-[11px] text-text-placeholder mt-1.5">
          of {formatCurrency(total)} {team ? 'team total' : 'total'}
        </p>
        {/* The member's own contribution to the team's spend — the sub-line that makes a
            shared pool legible: "the bar moved, and this much of it was me". */}
        {team && (
          <p className="text-[11px] text-text-muted mt-1">
            <span className="text-text-placeholder">└─ </span>
            your spend <span className="font-semibold text-text-secondary">{formatCurrency(team.your_used)}</span>
            {used > 0 && <span className="text-text-placeholder"> · {team.your_share_of_used_pct}% of team</span>}
          </p>
        )}
        {/* Permanent vs today's grant — kept distinct so it's clear what persists vs what resets. */}
        {(allocation.permanent_cap !== undefined || allocation.overflow_granted_today !== undefined) && (
          <div className="flex flex-col gap-0.5 mt-2">
            {(allocation.permanent_cap ?? 0) > 0 && (
              <p className="text-[11px] text-text-muted">
                {team ? 'Team permanent cap' : 'Permanent cap'}{' '}
                <span className="font-semibold text-text-secondary">{formatCurrency(allocation.permanent_cap ?? 0)}</span>
                <span className="text-text-placeholder"> · persists daily</span>
              </p>
            )}
            {(allocation.overflow_granted_today ?? 0) > 0 && (
              <p className="text-[11px] text-brand-blue">
                {team ? 'Team overflow grant today' : "Today's overflow grant"}{' '}
                <span className="font-semibold">{formatCurrency(allocation.overflow_granted_today ?? 0)}</span>
                <span className="opacity-70"> · resets 00:00 UTC</span>
              </p>
            )}
          </div>
        )}
      </div>

      {drawingOverflow && (allocation.overflow_used ?? 0) > 0 && (
        <p className="text-xs text-brand-blue mb-3">
          +{formatCurrency(allocation.overflow_used ?? 0)} {team ? 'of your spend' : ''} from overflow today
        </p>
      )}

      {pool && (
        <div className="mb-3">
          <OverflowStatus allocation={allocation} pool={pool} />
        </div>
      )}

      {/* Action */}
      {!readOnly && (
        <>
          <div className="mt-auto border-t border-gray-100 mt-3 mb-3" />
          <Button variant="primary" disabled={paused} onClick={() => setShowRequestCap(true)} className="w-full text-xs">
            Request Cap
          </Button>
          {paused && <p className="text-[10px] text-amber-600 text-center mt-1">Offer is paused</p>}
          <RequestCapModal
            open={showRequestCap}
            onClose={() => setShowRequestCap(false)}
            geoId={allocation.geo_id}
            geoCode={allocation.geo_code}
            geoName={allocation.geo_name}
            unit={capUnit}
            requestPath={requestPath}
          />
        </>
      )}
    </Card>
  )
}
